/**
 * useSchoolApplications - Hook for fetching applications with server-side filtering
 *
 * This hook provides:
 * - Server-side filtering, sorting, and pagination via RPC
 * - Manages all filter/sort/pagination state internally
 * - Handles district-all mode automatically
 * - Debounced search
 * - Loading and error states
 * - Request cancellation for rapid filter changes
 * - Response caching with stale-while-revalidate
 * - Next page prefetching
 * - Background auto-refresh
 * - Optimized CSV export for large datasets
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserDistrictSchools } from '@/hooks/useUserDistrictSchools';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import { migrateLegacyNameSortKey, DEFAULT_NAME_SORT } from '@/components/school/shared/name-columns';
import {
  GetSchoolApplicationsParams,
  SchoolApplicationRow,
  TransformedApplication,
  transformApplicationRow,
  ACTIONABLE_STATUSES,
  DEFAULT_PAGE_SIZE,
} from '@/types/applications-rpc';

// ============================================================================
// Response Cache
// ============================================================================

interface CacheEntry {
  data: TransformedApplication[];
  totalCount: number;
  timestamp: number;
}

// Cache TTL in milliseconds (30 seconds) - data is "fresh" for this long
const CACHE_TTL = 30 * 1000;
// Stale TTL - data can be served stale for this long while revalidating (5 minutes)
const STALE_TTL = 5 * 60 * 1000;
// Max cache entries to prevent memory bloat
const MAX_CACHE_ENTRIES = 50;
// Background refresh interval (2 minutes)
const BACKGROUND_REFRESH_INTERVAL = 2 * 60 * 1000;

// In-memory cache for RPC responses
const responseCache = new Map<string, CacheEntry>();

type CacheStatus = 'fresh' | 'stale' | 'expired';

function createCacheKey(params: GetSchoolApplicationsParams): string {
  return JSON.stringify(params, Object.keys(params).sort());
}

function getCachedResponse(key: string): { entry: CacheEntry; status: CacheStatus } | null {
  const entry = responseCache.get(key);
  if (!entry) return null;

  const age = Date.now() - entry.timestamp;
  if (age > STALE_TTL) {
    responseCache.delete(key);
    return null;
  }

  const status: CacheStatus = age <= CACHE_TTL ? 'fresh' : 'stale';
  return { entry, status };
}

function setCachedResponse(key: string, data: TransformedApplication[], totalCount: number): void {
  if (responseCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    if (oldestKey) responseCache.delete(oldestKey);
  }
  responseCache.set(key, { data, totalCount, timestamp: Date.now() });
}

function clearCache(): void {
  responseCache.clear();
}

// Export for use in AuthContext logout
export { clearCache as clearSchoolApplicationsCache };

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export interface UseSchoolApplicationsOptions {
  schoolId: string | null;
  academicYear: string;
  statuses?: string[];
  enabled?: boolean;
  initialPageSize?: number;
}

export interface UseSchoolApplicationsReturn {
  // Data
  applications: TransformedApplication[];
  totalCount: number;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isBackgroundRefreshing: boolean;
  error: string | null;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Filters
  gradeFilter: string;
  setGradeFilter: (grade: string) => void;
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  hasSiblingFilter: boolean | null;
  setHasSiblingFilter: (value: boolean | null) => void;
  needsTransportFilter: boolean | null;
  setNeedsTransportFilter: (value: boolean | null) => void;
  specialNeedsFilter: boolean | null;
  setSpecialNeedsFilter: (value: boolean | null) => void;

  // Sorting
  sortField: string;
  setSortField: (field: string) => void;
  sortDirection: 'asc' | 'desc';
  setSortDirection: (direction: 'asc' | 'desc') => void;

  // Pagination
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  totalPages: number;

  // Actions
  refresh: () => Promise<void>;
  fetchApplications: () => Promise<void>;
  clearCache: () => void;

  // Export
  isExporting: boolean;
  exportProgress: { current: number; total: number };
  exportAllApplications: (onProgress?: (current: number, total: number) => void) => Promise<TransformedApplication[]>;

  // Status counts (computed from current filter state)
  statusCounts: {
    total: number;
    pendingAcceptance: number;
    accepted: number;
    enrolled: number;
  };
}

export function useSchoolApplications({
  schoolId,
  academicYear,
  statuses = [...ACTIONABLE_STATUSES],
  enabled = true,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseSchoolApplicationsOptions): UseSchoolApplicationsReturn {
  // Data state
  const [applications, setApplications] = useState<TransformedApplication[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  // Search state with debouncing
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filter state
  const [gradeFilter, setGradeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [hasSiblingFilter, setHasSiblingFilter] = useState<boolean | null>(null);
  const [needsTransportFilter, setNeedsTransportFilter] = useState<boolean | null>(null);
  const [specialNeedsFilter, setSpecialNeedsFilter] = useState<boolean | null>(null);

  // Sorting state - persist to localStorage.
  // Nothing stored means the roster opens by last name A-Z (the split-name
  // default); a stored key from before the split ('student_name') is migrated
  // onto the last-name column, which is what the combined column sorted by.
  const [sortField, setSortField] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('applicationsTab_sortField');
      const migrated = migrateLegacyNameSortKey(saved || null);
      if (migrated && migrated !== saved) {
        localStorage.setItem('applicationsTab_sortField', migrated);
      }
      return migrated ?? DEFAULT_NAME_SORT.key;
    }
    return DEFAULT_NAME_SORT.key;
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('applicationsTab_sortDirection');
      if (saved === 'asc' || saved === 'desc') return saved;
      // No stored direction: a stored field keeps the old newest-first
      // behaviour it was saved under; no stored field at all takes the
      // split-name default.
      return localStorage.getItem('applicationsTab_sortField') ? 'desc' : DEFAULT_NAME_SORT.direction;
    }
    return DEFAULT_NAME_SORT.direction;
  });

  // Pagination state - persist to localStorage
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('applicationsTab_itemsPerPage');
      return saved ? parseInt(saved, 10) : initialPageSize;
    }
    return initialPageSize;
  });

  // Persist sort preferences
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('applicationsTab_sortField', sortField);
      localStorage.setItem('applicationsTab_sortDirection', sortDirection);
    }
  }, [sortField, sortDirection]);

  // Persist pagination preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('applicationsTab_itemsPerPage', String(itemsPerPage));
    }
  }, [itemsPerPage]);

  // Get district schools for district-all mode
  const { data: districtSchools, isLoading: isLoadingDistrict } = useUserDistrictSchools(
    schoolId === 'district-all'
  );

  const isMountedRef = useRef(true);

  // AbortController for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track request ID to ignore stale responses
  const requestIdRef = useRef(0);
  // Track last cache key to prevent redundant state updates
  const lastCacheKeyRef = useRef<string | null>(null);
  // Ref to hold the latest fetchApplications function (prevents stale closure in intervals)
  const fetchApplicationsRef = useRef<(showRefreshing?: boolean, skipCache?: boolean) => Promise<void>>();

  // Build RPC params
  const buildParams = useCallback((): GetSchoolApplicationsParams => {
    const params: GetSchoolApplicationsParams = {
      p_academic_year: academicYear,
      p_statuses: activeFilter === 'all' ? statuses : [activeFilter],
      p_sort_field: sortField as GetSchoolApplicationsParams['p_sort_field'],
      p_sort_direction: sortDirection,
      p_limit: itemsPerPage,
      p_offset: (currentPage - 1) * itemsPerPage,
    };

    // School targeting
    if (schoolId === 'district-all') {
      params.p_school_ids = districtSchools?.map((s) => s.school_id) || [];
      params.p_school_id = null;
    } else if (schoolId) {
      params.p_school_id = schoolId;
      params.p_school_ids = null;
    }

    // Filters
    if (debouncedSearchQuery.trim()) {
      params.p_search_query = debouncedSearchQuery.trim();
    }
    if (gradeFilter && gradeFilter !== 'all') {
      params.p_grade = gradeFilter;
    }
    if (hasSiblingFilter !== null) {
      params.p_has_sibling = hasSiblingFilter;
    }
    if (needsTransportFilter !== null) {
      params.p_needs_transportation = needsTransportFilter;
    }
    if (specialNeedsFilter !== null) {
      params.p_special_transportation_needs = specialNeedsFilter;
    }

    return params;
  }, [
    schoolId,
    academicYear,
    statuses,
    debouncedSearchQuery,
    gradeFilter,
    activeFilter,
    hasSiblingFilter,
    needsTransportFilter,
    specialNeedsFilter,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
    districtSchools,
  ]);

  // Prefetch next page silently in background
  const prefetchNextPage = useCallback(
    async (currentTotalCount: number) => {
      if (!enabled || !schoolId || !academicYear) return;
      if (schoolId === 'district-all' && (!districtSchools || districtSchools.length === 0)) return;

      const totalPages = Math.ceil(currentTotalCount / itemsPerPage);
      if (currentPage >= totalPages) return;

      const nextPageParams = {
        ...buildParams(),
        p_offset: currentPage * itemsPerPage,
      };
      const cacheKey = createCacheKey(nextPageParams);

      if (getCachedResponse(cacheKey)) return;

      try {
        const { data, error: rpcError } = await supabase.rpc('get_school_applications', nextPageParams);
        if (rpcError || !isMountedRef.current) return;

        const rows = (data || []) as SchoolApplicationRow[];
        const transformed = rows.map(transformApplicationRow);
        const count = rows.length > 0 ? rows[0].total_count : 0;
        setCachedResponse(cacheKey, transformed, count);
      } catch {
        // Silently ignore prefetch errors
      }
    },
    [enabled, schoolId, academicYear, currentPage, itemsPerPage, buildParams, districtSchools]
  );

  // Fetch applications with request cancellation and caching
  const fetchApplications = useCallback(
    async (showRefreshing = false, skipCache = false) => {
      if (!enabled || !schoolId || !academicYear) {
        return;
      }

      // Wait for district schools if in district-all mode
      if (schoolId === 'district-all' && (!districtSchools || districtSchools.length === 0)) {
        if (!isLoadingDistrict) {
          setApplications([]);
          setTotalCount(0);
        }
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const currentRequestId = ++requestIdRef.current;

      const params = buildParams();
      const cacheKey = createCacheKey(params);

      // Check cache first (unless explicitly skipping)
      if (!skipCache) {
        const cached = getCachedResponse(cacheKey);
        if (cached) {
          if (lastCacheKeyRef.current !== cacheKey) {
            lastCacheKeyRef.current = cacheKey;
            setApplications(cached.entry.data);
            setTotalCount(cached.entry.totalCount);
          }
          prefetchNextPage(cached.entry.totalCount);

          // If fresh, don't revalidate
          if (cached.status === 'fresh') {
            return;
          }
          // Stale - serve it but continue to revalidate in background
        }
      }

      // Show loading state only if no cached data
      if (!getCachedResponse(cacheKey)) {
        if (showRefreshing) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }
      }
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc('get_school_applications', params);

        if (abortController.signal.aborted || currentRequestId !== requestIdRef.current) {
          return;
        }

        if (rpcError) {
          throw rpcError;
        }

        if (isMountedRef.current) {
          const rows = (data || []) as SchoolApplicationRow[];
          const transformed = rows.map(transformApplicationRow);
          const count = rows.length > 0 ? rows[0].total_count : 0;

          setCachedResponse(cacheKey, transformed, count);
          lastCacheKeyRef.current = cacheKey;
          setApplications(transformed);
          setTotalCount(count);
          prefetchNextPage(count);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching applications:', err);
        EnhancedErrorLogger.getInstance().logQuickError(err instanceof Error ? err : new Error(String(err)), 'SchoolApplications', { context: 'fetch applications' });
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load applications';
          setError(errorMessage);
        }
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [enabled, schoolId, academicYear, districtSchools, isLoadingDistrict, buildParams, prefetchNextPage]
  );

  // Keep fetchApplications ref updated (for use in intervals without causing recreations)
  fetchApplicationsRef.current = fetchApplications;

  // Refresh function - skips cache to get fresh data
  const refresh = useCallback(async () => {
    await fetchApplications(true, true);
  }, [fetchApplications]);

  // Export all matching applications (fetches in chunks, bypasses pagination)
  const exportAllApplications = useCallback(
    async (onProgress?: (current: number, total: number) => void): Promise<TransformedApplication[]> => {
      if (!enabled || !schoolId || !academicYear) {
        return [];
      }

      if (schoolId === 'district-all' && (!districtSchools || districtSchools.length === 0)) {
        return [];
      }

      setIsExporting(true);
      setExportProgress({ current: 0, total: totalCount });

      const EXPORT_CHUNK_SIZE = 500;
      const allApplications: TransformedApplication[] = [];
      let offset = 0;
      let fetchedTotal = 0;

      try {
        const baseParams = buildParams();

        while (true) {
          const chunkParams = {
            ...baseParams,
            p_limit: EXPORT_CHUNK_SIZE,
            p_offset: offset,
          };

          const { data, error: rpcError } = await supabase.rpc('get_school_applications', chunkParams);

          if (rpcError) {
            throw rpcError;
          }

          const rows = (data || []) as SchoolApplicationRow[];
          if (rows.length === 0) break;

          const transformed = rows.map(transformApplicationRow);
          allApplications.push(...transformed);

          if (fetchedTotal === 0 && rows.length > 0) {
            fetchedTotal = rows[0].total_count;
            setExportProgress({ current: allApplications.length, total: fetchedTotal });
          }

          const progress = { current: allApplications.length, total: fetchedTotal };
          setExportProgress(progress);
          onProgress?.(progress.current, progress.total);

          if (rows.length < EXPORT_CHUNK_SIZE || allApplications.length >= fetchedTotal) {
            break;
          }

          offset += EXPORT_CHUNK_SIZE;
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        return allApplications;
      } catch (err) {
        console.error('Error exporting applications:', err);
        EnhancedErrorLogger.getInstance().logQuickError(err instanceof Error ? err : new Error(String(err)), 'SchoolApplications', { context: 'update application' });
        throw err;
      } finally {
        setIsExporting(false);
        setExportProgress({ current: 0, total: 0 });
      }
    },
    [enabled, schoolId, academicYear, totalCount, buildParams, districtSchools]
  );

  // Auto-fetch on dependency changes
  useEffect(() => {
    fetchApplications();
  }, [
    schoolId,
    academicYear,
    debouncedSearchQuery,
    gradeFilter,
    activeFilter,
    hasSiblingFilter,
    needsTransportFilter,
    specialNeedsFilter,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
    districtSchools,
  ]);

  // Reset page when filters or items-per-page change (but not on sort change)
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchQuery,
    gradeFilter,
    activeFilter,
    hasSiblingFilter,
    needsTransportFilter,
    specialNeedsFilter,
    itemsPerPage,
  ]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Background auto-refresh when tab is visible
  // Uses ref to avoid recreating interval when fetchApplications changes
  useEffect(() => {
    if (!enabled) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startBackgroundRefresh = () => {
      if (intervalId) clearInterval(intervalId);

      intervalId = setInterval(async () => {
        if (document.hidden || isLoading || isRefreshing || isExporting) return;

        setIsBackgroundRefreshing(true);
        try {
          // Use ref to get latest function
          await fetchApplicationsRef.current?.(false, true);
        } finally {
          if (isMountedRef.current) {
            setIsBackgroundRefreshing(false);
          }
        }
      }, BACKGROUND_REFRESH_INTERVAL);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        startBackgroundRefresh();
      }
    };

    startBackgroundRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isLoading, isRefreshing, isExporting]); // Removed fetchApplications - using ref instead

  // Compute status counts - accurate when filtered to a specific status,
  // or when all data fits on one page. Otherwise marked as unknown (-1).
  const statusCounts = useMemo(() => {
    const counts = {
      total: totalCount,
      pendingAcceptance: -1,
      accepted: -1,
      enrolled: -1,
    };

    if (activeFilter !== 'all') {
      // When filtered to a specific status, totalCount IS the accurate count
      if (activeFilter === 'pending_acceptance') counts.pendingAcceptance = totalCount;
      else if (activeFilter === 'accepted') counts.accepted = totalCount;
      else if (activeFilter === 'enrolled') counts.enrolled = totalCount;
    } else if (applications.length >= totalCount) {
      // All data fits on one page - per-status counts are accurate
      counts.pendingAcceptance = 0;
      counts.accepted = 0;
      counts.enrolled = 0;
      applications.forEach((app) => {
        if (app.status === 'pending_acceptance') counts.pendingAcceptance++;
        else if (app.status === 'accepted') counts.accepted++;
        else if (app.status === 'enrolled') counts.enrolled++;
      });
    }
    // else: counts remain -1 (unknown) for multi-page "all" view

    return counts;
  }, [applications, totalCount, activeFilter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  return {
    // Data
    applications,
    totalCount,

    // Loading states
    isLoading,
    isRefreshing,
    isBackgroundRefreshing,
    error,

    // Search
    searchQuery,
    setSearchQuery,

    // Filters
    gradeFilter,
    setGradeFilter,
    activeFilter,
    setActiveFilter,
    hasSiblingFilter,
    setHasSiblingFilter,
    needsTransportFilter,
    setNeedsTransportFilter,
    specialNeedsFilter,
    setSpecialNeedsFilter,

    // Sorting
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,

    // Actions
    refresh,
    fetchApplications,
    clearCache,

    // Export
    isExporting,
    exportProgress,
    exportAllApplications,

    // Status counts
    statusCounts,
  };
}

export default useSchoolApplications;
